/*
  GoRoam homepage behaviour (loaded by sections/gr-stack.liquid).
  - Live multiplier: fills every [data-mult-*] and [data-entry-tag], shows [data-bonus-only].
  - Countdown in the hero ([data-urgency]): Giveaway Gator dates first, theme dates as fallback.
  - Quick entries: tier selection, entry math, free gifts, add to cart, phone bottom bar.
  - Basecamp passes: add a membership product with its selling plan.
  Entry formula (Official Rules): floor(price) x multiplier + tier bonus entries.
*/
import { CartUpdatedEvent } from '@theme/events';

const fmt = (n) => Math.round(n).toLocaleString('en-US');
const money = (n) => '$' + (n % 1 ? n.toFixed(2) : String(n));

function whenEntries(cb) {
  if (window.GoRoamEntries) return window.GoRoamEntries.ready(cb);
  let tries = 0;
  const t = setInterval(() => {
    if (window.GoRoamEntries) { clearInterval(t); window.GoRoamEntries.ready(cb); }
    else if (++tries > 50) { clearInterval(t); cb(null); }
  }, 100);
}

/* ---------- cart ---------- */
async function addToCart(btn, item, source) {
  if (!item.id || btn.getAttribute('aria-busy') === 'true') return;
  btn.setAttribute('aria-busy', 'true');
  const sections = ['auto-gifts'];
  const drawer = document.querySelector('cart-drawer');
  if (drawer?.dataset.sectionId) sections.push(drawer.dataset.sectionId);
  try {
    const res = await fetch(Theme.routes.cart_add_url, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify({ items: [item], sections: sections.join(',') }),
    });
    const json = await res.json();
    if (!res.ok) throw new Error(json.description || json.message || 'Could not add to cart');
    if (location.pathname.includes('/cart')) { location.reload(); return; }
    const count = document.querySelector('cart-icon')?.getCurrentItemCount?.();
    document.dispatchEvent(new CartUpdatedEvent({}, { itemCount: count != null ? count + 1 : undefined, source, sections: json.sections }));
  } catch (err) {
    console.error('GoRoam:', err);
    document.dispatchEvent(new CustomEvent('toast:open', { detail: { type: 'error', message: err.message } }));
  } finally {
    btn.removeAttribute('aria-busy');
  }
}

/* ---------- multiplier everywhere ---------- */
function applyMultiplier(root, mult) {
  const bonus = mult > 1;
  root.querySelectorAll('[data-bonus-only]').forEach((el) => { el.hidden = !bonus; });
  root.querySelectorAll('[data-base-only]').forEach((el) => { el.hidden = bonus; });
  root.querySelectorAll('[data-mult-x]').forEach((el) => { el.textContent = fmt(mult) + 'X'; });
  root.querySelectorAll('[data-mult-n]').forEach((el) => { el.textContent = fmt(mult); });
  root.querySelectorAll('[data-mult-ex]').forEach((el) => { el.textContent = fmt(Math.floor(parseFloat(el.dataset.price) || 0) * mult); });
  root.querySelectorAll('[data-entry-tag]').forEach((el) => {
    el.textContent = '+' + fmt(Math.floor(parseFloat(el.dataset.price) || 0) * mult) + ' entries';
  });
}

/* ---------- countdown ---------- */
function initUrgency(el, getMult) {
  const timeEl = el.querySelector('[data-urg-time]');
  const fill = el.querySelector('[data-urg-fill]');
  const labelEl = el.querySelector('[data-urg-label]');
  const subEl = el.querySelector('[data-urg-sub]');
  const endTextEl = el.querySelector('[data-urg-end-text]');
  const srEl = el.querySelector('[data-urg-sr]');
  const finalMs = (parseInt(el.dataset.finalMinutes, 10) || 60) * 60000;
  const pad = (n) => (n < 10 ? '0' : '') + n;
  let start = null, end = null, timer = null;

  const fmtEnd = (ms) => {
    try {
      // Always Mountain time: the giveaway runs on MST, whatever the shopper's clock says.
      return 'Ends ' + new Date(ms).toLocaleString('en-US', { timeZone: 'America/Denver', month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit', timeZoneName: 'short' });
    } catch (e) { return ''; }
  };
  const esc = (s) => String(s).replace(/[&<>"]/g, (c) => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;' }[c]));
  // {X} becomes the multiplier chip (<b class="grh-x">100X</b>), {N} the plain number.
  const fill_ = (s) => esc(s).replace('{X}', '<b class="grh-x">' + fmt(getMult()) + 'X</b>').replace('{N}', fmt(getMult()));

  function setTarget(t) {
    start = t.start; end = t.end;
    if (t.label) labelEl.innerHTML = fill_(t.label);
    if (t.sub) subEl.innerHTML = fill_(t.sub);
    if (t.endText != null) endTextEl.textContent = t.endText;
    srEl.textContent = labelEl.textContent + ' ' + endTextEl.textContent;
  }

  // Manual dates from the theme editor.
  const manual = {
    start: Date.parse(el.dataset.start), end: Date.parse(el.dataset.end),
    label: el.dataset.label, sub: el.dataset.sub, endText: el.dataset.endText,
  };

  function choose() {
    const now = Date.now();
    if (el.dataset.source === 'auto' && window.GoRoamEntries?.schedule) {
      // Gator is on the page but reports no live giveaway: show nothing rather than the manual dates.
      const st = window.GoRoamEntries.status ? window.GoRoamEntries.status() : { present: false };
      if (st.present && !st.active && getMult() <= 1) { start = null; end = null; return; }
      const s = window.GoRoamEntries.schedule();
      if (s.bonusEnd && s.bonusEnd > now && getMult() > 1) {
        return setTarget({ start: s.bonusStart || now, end: s.bonusEnd, label: el.dataset.labelBonus, sub: el.dataset.subBonus, endText: fmtEnd(s.bonusEnd) });
      }
      if (s.giveawayEnd && s.giveawayEnd > now) {
        return setTarget({ start: null, end: s.giveawayEnd, label: el.dataset.labelEnd, sub: el.dataset.subEnd, endText: fmtEnd(s.giveawayEnd) });
      }
    }
    setTarget(manual);
  }

  function tick() {
    if (!end || isNaN(end)) { el.hidden = true; return; }
    const left = end - Date.now();
    if (left <= 0) { el.hidden = true; clearInterval(timer); return; }
    el.hidden = false;
    const d = Math.floor(left / 864e5), h = Math.floor((left % 864e5) / 36e5),
      m = Math.floor((left % 36e5) / 6e4), s = Math.floor((left % 6e4) / 1e3);
    timeEl.innerHTML = (d ? d + '<small>d</small>' : '') + pad(h) + '<small>h</small>' + pad(m) + '<small>m</small>' + (d ? '' : pad(s) + '<small>s</small>');
    const span = (start && !isNaN(start) && start < end) ? end - start : 30 * 864e5;
    fill.style.width = (Math.max(0.02, Math.min(1, left / span)) * 100) + '%';
    el.classList.toggle('is-final', left < finalMs);
  }

  return {
    source: el.dataset.source,
    refresh() { choose(); clearInterval(timer); timer = setInterval(tick, 1000); tick(); },
  };
}

/* ---------- quick entries ---------- */
function initQuickEntries(qe, getMult) {
  const tiers = [...qe.querySelectorAll('[data-tier]')];
  if (!tiers.length) return;
  const $ = (s) => qe.querySelector(s);
  const mName = $('[data-m-name]'), mPrice = $('[data-m-price]'), mRows = $('[data-m-rows]'),
    mTotal = $('[data-m-total]'), mGifts = $('[data-m-gifts]'), mAdd = $('[data-m-add]');
  const dock = $('[data-dock]'), dTotal = $('[data-d-total]'), dAdd = $('[data-d-add]');
  let current = tiers.find((t) => t.hasAttribute('data-default')) || tiers[Math.min(2, tiers.length - 1)];

  const data = (t) => ({
    name: t.dataset.name, price: parseFloat(t.dataset.price) || 0, bonus: parseInt(t.dataset.bonus, 10) || 0,
    variant: t.dataset.variant, available: t.dataset.available !== 'false',
    gifts: (() => { try { return JSON.parse(t.dataset.gifts || '[]'); } catch (e) { return []; } })(),
  });

  function paintCards() {
    const m = getMult();
    tiers.forEach((t) => {
      const d = data(t), base = Math.floor(d.price), total = base * m + d.bonus;
      t.querySelector('[data-total]').textContent = fmt(total);
      const changed = (m > 1 || d.bonus > 0);
      const was = t.querySelector('[data-was]'); was.hidden = !changed;
      was.textContent = fmt(base) + ' entries';
      const lbl = t.querySelector('[data-lbl]'); if (lbl) lbl.hidden = !changed;
      const how = t.querySelector('[data-how]'); how.hidden = !changed;
      // "80X + 150 bonus" while a multiplier is live; "+150 bonus entries" otherwise.
      how.innerHTML = m > 1
        ? '<b class="grh-x">' + fmt(m) + 'X</b>' + (d.bonus ? ' + ' + fmt(d.bonus) + ' bonus' : '')
        : (d.bonus ? '+' + fmt(d.bonus) + ' bonus entries' : '');
    });
  }

  function select(t) {
    current = t;
    tiers.forEach((x) => x.setAttribute('aria-pressed', String(x === t)));
    const d = data(t), m = getMult(), base = Math.floor(d.price), total = base * m + d.bonus;
    mName.textContent = d.name + ' quick entry';
    mPrice.textContent = money(d.price);
    let rows = '<li><span>Base entries ($1 = 1)</span><span>' + fmt(base) + '</span></li>';
    if (m > 1) rows += '<li><span>× <b class="grh-x">' + fmt(m) + 'X</b> bonus multiplier</span><span>' + fmt(base * m) + '</span></li>';
    if (d.bonus) rows += '<li><span>+ ' + d.name + ' bonus entries</span><span>' + fmt(d.bonus) + '</span></li>';
    mRows.innerHTML = rows;
    mTotal.textContent = fmt(total);
    mGifts.innerHTML = d.gifts.map((g) => '<div class="grh-gift">' + (g.img ? '<img src="' + g.img + '" alt="" loading="lazy">' : '') +
      '<b></b>Free <s>' + money(g.v) + '</s></div>').join('');
    [...mGifts.querySelectorAll('b')].forEach((b, i) => { b.textContent = d.gifts[i].n; });
    const label = d.available ? 'Add ' + d.name + ' · ' + money(d.price) : 'Sold out';
    mAdd.textContent = label; mAdd.disabled = !d.available;
    if (dock) { dTotal.textContent = fmt(total); dAdd.textContent = label; dAdd.disabled = !d.available; }
  }

  const add = (btn) => addToCart(btn, { id: Number(data(current).variant), quantity: 1 }, 'gr-quick-entries');
  const panel = qe.querySelector('.grh-panel') || mAdd;
  const reveal = () => {
    // On phones the maths panel sits below the tier cards; bring it into view so the choice
    // visibly does something and the Add button is right there.
    if (window.innerWidth >= 900) return;
    const r = panel.getBoundingClientRect();
    if (r.top >= 0 && r.bottom <= window.innerHeight) return;
    const y = window.scrollY + r.top - 72;
    window.scrollTo({ top: y, behavior: 'smooth' });
  };
  tiers.forEach((t) => t.addEventListener('click', () => { select(t); reveal(); }));
  mAdd.addEventListener('click', () => add(mAdd));
  dAdd?.addEventListener('click', () => add(dAdd));

  // Bottom bar: on once the hero is out of view, off while the math panel is on screen.
  if (dock && 'IntersectionObserver' in window) {
    const hero = document.querySelector('.grh-hero');
    let heroGone = !hero, mathSeen = false;
    const update = () => {
      const on = heroGone && !mathSeen;
      dock.classList.toggle('is-on', on);
      dock.setAttribute('aria-hidden', String(!on));
      dAdd.tabIndex = on ? 0 : -1;
    };
    if (hero) new IntersectionObserver(([e]) => { heroGone = !e.isIntersecting; update(); }).observe(hero);
    new IntersectionObserver(([e]) => { mathSeen = e.isIntersecting; update(); }).observe(mAdd);
  }

  return { repaint: () => { paintCards(); select(current); } };
}

/* ---------- membership passes ---------- */
function initPasses(root) {
  root.querySelectorAll('[data-pass-add]').forEach((btn) => {
    btn.addEventListener('click', () => {
      const item = { id: Number(btn.dataset.variant), quantity: 1 };
      if (btn.dataset.plan) item.selling_plan = Number(btn.dataset.plan);
      addToCart(btn, item, 'gr-membership');
    });
  });
}

/* ---------- boot ---------- */
document.querySelectorAll('.grh').forEach((root) => {
  const fallback = parseFloat(root.dataset.fallbackMultiplier) || 0;
  let mult = fallback > 1 ? fallback : 1;
  const getMult = () => mult;

  const urgs = [...root.querySelectorAll('[data-urgency]')].map((el) => initUrgency(el, getMult));
  const qes = [...root.querySelectorAll('[data-qe]')].map((qe) => initQuickEntries(qe, getMult)).filter(Boolean);
  initPasses(root);
  // Countdowns in "auto" mode wait for Giveaway Gator before showing anything, so a manual
  // fallback never flashes while Gator is still loading.
  let resolved = false;
  const paint = () => {
    applyMultiplier(root, mult); qes.forEach((q) => q.repaint());
    urgs.forEach((u) => { if (resolved || u.source !== 'auto') u.refresh(); });
  };
  paint();

  whenEntries((m) => {
    if (m && m > 0) mult = m;
    resolved = true;
    paint();
  });
});
